/* Keep Shopify's native order within each availability group, before pagination. */
(() => {
  function orderedPage(products, page, size) {
    if (!Number.isInteger(page) || page < 1 || !Number.isInteger(size) || size < 1) throw Error('Invalid page');
    if (new Set(products.map(p => p.id)).size !== products.length || products.some(p => typeof p.available !== 'boolean')) throw Error('Invalid product index');
    const ordered = products.filter(p => p.available).concat(products.filter(p => !p.available));
    return ordered.slice((page - 1) * size, page * size);
  }
  function searchPlan(native, promoted, page, size) {
    // Search promotion adds cards on page 1 and removes matches from their native pages.
    // Keep those page capacities and the original membership, even when a page is empty.
    orderedPage(native, page, size);
    orderedPage(promoted, 1, size);
    const promotions = new Map(promoted.map(p => [p.id, p]));
    const capacities = Array(Math.max(1, Math.ceil(native.length / size))).fill(0);
    capacities[0] = promoted.length;
    const sourcePages = new Map(promoted.map(p => [p.id, 1]));
    const items = promoted.slice();
    native.forEach((item, index) => {
      if (promotions.has(item.id)) {
        if (promotions.get(item.id).available !== item.available) throw Error('Inventory changed');
        return;
      }
      const source = Math.floor(index / size) + 1;
      capacities[source - 1]++;
      sourcePages.set(item.id, source);
      items.push(item);
    });
    const ordered = items.filter(p => p.available).concat(items.filter(p => !p.available));
    const start = capacities.slice(0, page - 1).reduce((sum, n) => sum + n, 0);
    return { wanted: ordered.slice(start, start + (capacities[page - 1] || 0)), sourcePages, pages: capacities.length };
  }
  // Exposed for deterministic pagination tests; no store data is persisted.
  globalThis.CollectionAvailabilityCore = { orderedPage, searchPlan };
  if (typeof document === 'undefined' || window.CollectionAvailability) return;
  let active, searchSnapshot;
  const searchKey = href => {
    const url = new URL(href, location.origin);
    url.searchParams.delete('page'); url.searchParams.delete('section_id'); url.searchParams.sort();
    return url.href;
  };
  const parse = html => new DOMParser().parseFromString(html, 'text/html');
  const data = (doc, selector) => {
    const node = doc.querySelector(selector);
    if (!node) throw Error('Missing collection metadata');
    return JSON.parse(node.textContent);
  };
  async function mapLimit(items, work) {
    const results = new Array(items.length);
    let cursor = 0;
    await Promise.all(Array.from({ length: Math.min(3, items.length) }, async () => {
      while (cursor < items.length) { const i = cursor++; results[i] = await work(items[i]); }
    }));
    return results;
  }
  async function prepare(html, href, signal, refresh = false) {
    const doc = parse(html), node = doc.querySelector('[data-ranking-page]');
    if (!node) return html;
    const meta = JSON.parse(node.textContent);
    const isSearch = meta.kind === 'search';
    if (!meta.count && !isSearch) return html;
    const base = new URL(href, location.origin);
    const fetchSection = async (section, page) => {
      const url = new URL(base);
      url.searchParams.set('section_id', section);
      url.searchParams.set('page', String(page));
      for (let attempt = 0; attempt < 3; attempt++) {
        const response = await fetch(url, { signal, credentials: 'same-origin', cache: 'no-store' });
        if (response.ok) return parse(await response.text());
        if (attempt === 2 || (response.status !== 429 && response.status < 500)) throw Error('Collection request failed');
        const seconds = Math.min(5, Math.max(1, Number(response.headers.get('Retry-After')) || (attempt + 1)));
        await new Promise((resolve, reject) => {
          const abort = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
          const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, seconds * 1000);
          signal.addEventListener('abort', abort, { once: true });
          if (signal.aborted) abort();
        });
      }
      throw Error('Collection request failed');
    };
    if (isSearch) return prepareSearch(doc, meta, fetchSection, refresh, base, signal);
    const first = data(await fetchSection('collection-availability-index', 1), '[data-ranking-index]');
    if (first.count !== meta.count || first.page !== 1 || first.pages < 1) throw Error('Results changed');
    const remaining = await mapLimit(Array.from({ length: first.pages - 1 }, (_, i) => i + 2), async page => {
      const result = data(await fetchSection('collection-availability-index', page), '[data-ranking-index]');
      if (result.count !== first.count || result.pages !== first.pages || result.page !== page) throw Error('Collection changed');
      return result.products;
    });
    const products = first.products.concat(...remaining);
    if (products.length !== meta.count) throw Error('Incomplete collection index');
    const wanted = orderedPage(products, meta.page, meta.size);
    const originalPages = new Map(products.map((p, i) => [p.id, Math.floor(i / meta.size) + 1]));
    const pages = [...new Set(wanted.map(p => originalPages.get(p.id)))].filter(p => p !== meta.page);
    const documents = [doc, ...await mapLimit(pages, page => fetchSection(meta.section, page))];
    const cards = new Map();
    for (const fragment of documents) {
      const pageData = data(fragment, '[data-ranking-page]');
      if (pageData.count !== meta.count || pageData.size !== meta.size) throw Error('Collection changed');
      for (const card of fragment.querySelectorAll('#product-grid > [data-ranking-id]')) cards.set(card.dataset.rankingId, card);
    }
    for (const product of wanted) {
      const card = cards.get(product.id);
      if (!card || (card.dataset.rankingAvailable === 'true') !== product.available) throw Error('Inventory changed');
    }
    const grid = doc.getElementById('product-grid');
    if (!grid) throw Error('Missing product grid');
    // Preserve collection promotion slots, but replace each product slot.
    const slots = [...grid.children];
    let index = 0;
    for (const slot of slots) {
      if (!slot.hasAttribute('data-ranking-id')) continue;
      const product = wanted[index++];
      if (product) slot.replaceWith(cards.get(product.id).cloneNode(true));
      else slot.remove();
    }
    if (index < wanted.length) throw Error('Incomplete product grid');
    grid.dataset.availabilitySorted = 'true';
    grid.dataset.rankingPage = String(meta.page);
    grid.dataset.rankingPages = String(Math.ceil(meta.count / meta.size));
    return doc.documentElement.outerHTML;
  }
  async function prepareSearch(doc, meta, fetchSection, refresh, base, signal) {
    const key = searchKey(base), promotedSignature = JSON.stringify(meta.promoted);
    const saved = searchSnapshot?.key === key ? searchSnapshot : null;
    if (saved && (saved.count !== meta.count || saved.promotedSignature !== promotedSignature)) throw Error('Search changed; refresh required');
    let products, cards;
    if (saved && !(refresh && meta.page === 1)) {
      ({ products, cards } = saved);
    } else {
    const firstDoc = meta.page === 1 && !refresh ? doc : await fetchSection(meta.section, 1);
    const first = data(firstDoc, '[data-search-snapshot]');
    if (first.page !== 1 || first.count !== meta.count || first.pages < (meta.count ? 1 : 0)) throw Error('Search changed');
    const fragments = [firstDoc, ...await mapLimit(Array.from({ length: Math.max(0, first.pages - 1) }, (_, i) => i + 2), page => fetchSection(meta.section, page))];
    const tails = meta.cardTailSection ? await mapLimit([...fragments.entries()], async ([index, fragment]) => {
      const snapshot = data(fragment, '[data-search-snapshot]');
      if (snapshot.products.length <= 150) return null;
      const tail = await fetchSection(meta.cardTailSection, index + 1);
      const tailMeta = data(tail, '[data-search-tail]');
      if (tailMeta.page !== index + 1 || tailMeta.count !== meta.count) throw Error('Search changed');
      return tail.querySelector('[data-search-tail-cards]');
    }) : [];
    products = []; cards = new Map();
    // Keep the existing promotion cards, including products outside the native query.
    for (const card of firstDoc.querySelectorAll('#product-grid > [data-ranking-id]')) {
      if (meta.promoted.some(item => item.id === card.dataset.rankingId)) cards.set(card.dataset.rankingId, card);
    }
    fragments.forEach((fragment, index) => {
      const snapshot = data(fragment, '[data-search-snapshot]');
      const pageMeta = data(fragment, '[data-ranking-page]');
      if (snapshot.page !== index + 1 || snapshot.pages !== first.pages || snapshot.count !== meta.count || JSON.stringify(pageMeta.promoted) !== JSON.stringify(meta.promoted)) throw Error('Search changed');
      const template = fragment.querySelector('[data-search-snapshot-cards]');
      if (!template) throw Error('Missing search cards');
      // Main-grid cards are reused; the inert template contains only the missing cards.
      const localCards = new Map([...fragment.querySelectorAll('#product-grid > [data-ranking-id]'), ...template.content.querySelectorAll('[data-ranking-id]')].map(card => [card.dataset.rankingId, card]));
      if (tails[index]) for (const card of tails[index].content.querySelectorAll('[data-ranking-id]')) localCards.set(card.dataset.rankingId, card);
      snapshot.products.forEach(item => {
        const card = localCards.get(item.id);
        if (!card || (card.dataset.rankingAvailable === 'true') !== item.available) throw Error('Search inventory changed');
        if (!cards.has(item.id)) cards.set(item.id, card);
      });
      products.push(...snapshot.products);
    });
    if (products.length !== meta.count) throw Error('Incomplete search snapshot');
    // Freeze one query while scrolling: Shopify can reorder equally relevant hits between requests.
    orderedPage(products, 1, meta.size);
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    searchSnapshot = { key, count: meta.count, promotedSignature, products, cards };
    }
    const plan = searchPlan(products, meta.promoted, meta.page, meta.size);
    const grid = doc.getElementById('product-grid');
    if (!grid) throw Error('Missing search grid');
    const selected = plan.wanted.map(item => {
      const card = cards.get(item.id);
      if (!card || (card.dataset.rankingAvailable === 'true') !== item.available) throw Error('Search inventory changed');
      return card.cloneNode(true);
    });
    grid.replaceChildren(...selected);
    grid.dataset.availabilitySorted = 'true';
    grid.dataset.rankingPage = String(meta.page);
    grid.dataset.rankingPages = String(plan.pages);
    const blueprint = doc.querySelector('[data-search-pagination]');
    if (blueprint) {
      const container = doc.getElementById('ProductGridContainer');
      container.querySelectorAll('.pagination--wrapper').forEach(node => node.remove());
      if (plan.pages > 1) {
        const pagination = blueprint.content.firstElementChild.cloneNode(true);
        const nav = pagination.querySelector('[data-search-page-links]');
        if (nav) {
          const link = page => { const url = new URL(base); url.searchParams.delete('section_id'); url.searchParams.set('page', String(page)); return url.pathname + url.search; };
          const previous = nav.querySelector('[data-search-prev]'), next = nav.querySelector('[data-search-next]');
          for (const [anchor, page, disabled] of [[previous, meta.page - 1, meta.page <= 1], [next, meta.page + 1, meta.page >= plan.pages]]) {
            if (disabled) { anchor.parentElement.classList.add('disabled'); anchor.setAttribute('aria-disabled', 'true'); }
            else anchor.setAttribute('href', link(page));
          }
          const pages = [...new Set([1, ...Array.from({ length: 5 }, (_, i) => meta.page - 2 + i), plan.pages])].filter(page => page > 0 && page <= plan.pages).sort((a, b) => a - b);
          let last = 0;
          for (const page of pages) {
            if (last && page > last + 1) { const gap = doc.createElement('li'); gap.className = 'ellip'; gap.textContent = '…'; nav.insertBefore(gap, next.parentElement); }
            const li = doc.createElement('li'), anchor = doc.createElement(page === meta.page ? 'span' : 'a');
            li.className = page === meta.page ? 'current' : 'page'; anchor.textContent = String(page);
            if (page === meta.page) anchor.setAttribute('aria-current', 'page'); else anchor.setAttribute('href', link(page));
            li.appendChild(anchor); nav.insertBefore(li, next.parentElement); last = page;
          }
        }
        container.appendChild(pagination);
      }
    }
    doc.querySelectorAll('[data-search-snapshot], [data-search-snapshot-cards], [data-search-pagination]').forEach(node => node.remove());
    // Display pages are smaller than Shopify's snapshot batches. Keep a small shell
    // so scrolling never requests a nonexistent native page or a different result set.
    if (!searchSnapshot.shell) {
      const shell = doc.cloneNode(true);
      shell.getElementById('product-grid').replaceChildren();
      searchSnapshot.shell = shell.documentElement.outerHTML;
      searchSnapshot.meta = { ...meta };
    }
    return doc.documentElement.outerHTML;
  }
  const api = window.CollectionAvailability = {
    begin() { active?.abort(); searchSnapshot = null; active = new AbortController(); return active.signal; },
    cancel() { active?.abort(); searchSnapshot = null; },
    async searchPage(href, signal) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      const saved = searchSnapshot;
      if (!saved?.shell || saved.key !== searchKey(href)) throw Error('Search snapshot expired; refresh required');
      const base = new URL(href, location.origin);
      const page = Number(base.searchParams.get('page') || 1);
      const meta = { ...saved.meta, page };
      if (!Number.isInteger(page) || page < 1 || page > searchPlan(saved.products, meta.promoted, 1, meta.size).pages) throw Error('Invalid search page');
      return prepareSearch(parse(saved.shell), meta, () => { throw Error('Unexpected search request'); }, false, base, signal);
    },
    async prepare(html, href, signal) {
      try { return await prepare(html, href, signal); }
      catch (error) {
        if (signal.aborted) throw error;
        // One fresh attempt handles a stock change between the index and card requests.
        return prepare(html, href, signal, true);
      }
    },
    showError() {
      const container = document.getElementById('ProductGridContainer');
      if (!container || container.querySelector('[data-ranking-error]')) return;
      const notice = document.createElement('p');
      notice.dataset.rankingError = '';
      notice.setAttribute('role', 'status');
      const chinese = document.documentElement.lang.startsWith('zh');
      notice.textContent = chinese ? '商品排序暂未更新，请刷新页面重试。' : 'Product order could not be updated. Please refresh to retry.';
      container.prepend(notice);
      container.querySelector('.collection')?.classList.remove('loading');
    },
    async initial() {
      const container = document.getElementById('ProductGridContainer');
      if (!container?.querySelector('[data-ranking-page]')) return;
      const signal = api.begin();
      container.querySelector('.collection')?.classList.add('loading');
      try {
        const html = await api.prepare(document.documentElement.outerHTML, location.href, signal);
        if (signal.aborted) return;
        const replacement = parse(html).getElementById('ProductGridContainer');
        container.innerHTML = replacement.innerHTML;
        container.querySelector('.collection')?.classList.remove('loading');
        container.querySelectorAll('.product-featured-image-link').forEach(n => { n.style.opacity = '1'; n.style.transform = 'none'; });
        document.dispatchEvent(new CustomEvent('collection:availability-sorted'));
      } catch (error) { if (!signal.aborted) api.showError(); }
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', api.initial, { once: true });
  else api.initial();
})();
